import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggerService } from '@app/blocks/logger/services/logger.service';
import moment from 'moment';

@Injectable({
   providedIn: 'root',
})
export class ApplicationFormTransformService {
   constructor(private logger: LoggerService) {}

   getApplicantTransform(response: HttpResponse<any>): any {
      if (response.status === 200) {
         const applicantResponse = response.body;
         this.logger.info('apiModel', applicantResponse);

         // use rest to pull out all properties already mapped to controls, re-assign the exclusions
         const {
            probity,
            birthDate,
            currentAddress,
            previousAddresses,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            addresses, // not required in a form model or api req?
            ...rest
         } = applicantResponse;

         const applicant = rest;

         applicant.totalEquity = () => {
            return applicant.homeValue + applicant.savingsValue + applicant.equityReleaseValue;
         };

         // probity
         if (probity) {
            applicant.isProbity = probity.isAssociated;
            applicant.probityConnectionTypeId = probity.probityConnectionTypeId;
            applicant.probityTitle = probity.probityTitle;
            applicant.probityFirstName = probity.probityFirstName;
            applicant.probityLastName = probity.probityLastName;
            applicant.probityRelationship = probity.probityRelationship;
            applicant.probityEmployeeRole = probity.probityEmployeeRole;
            applicant.probityPositionOnBoard = probity.probityPositionOnBoard;
            applicant.probityJobTitle = probity.probityJobTitle;
         }

         // dateOfBirth
         applicant.dateOfBirth = moment.utc(birthDate);

         if (applicant.dateOfBirth == '0001-01-01T06:00:00Z') {
            applicant.dateOfBirth = null;
         }

         applicant.dateOfBirth = applicant.dateOfBirth.add(6, 'h').format();

         // currentAddress
         if (currentAddress != null) {
            applicant.addressTypeId = currentAddress.addressTypeId;
            applicant.applicationContactAddressId = currentAddress.addressId;
            applicant.applicationContactAddress = currentAddress.applicationContactAddress;
            applicant.addressLine1 = currentAddress.addressLine1;
            applicant.addressLine2 = currentAddress.addressLine2;
            applicant.addressLine3 = currentAddress.addressLine3;
            applicant.city = currentAddress.addressCity;
            applicant.county = currentAddress.addressStateOrProvince;
            applicant.country = currentAddress.addressCountry;
            applicant.postCode = currentAddress.addressPostalCode;
            applicant.durationAtCurrentProperty = this.currentPropertyDuration(currentAddress.durationYears, currentAddress.durationMonths);
         }

         // previousAddresses
         applicant.previousAddresses = this.mapGetPreviousAddresses(previousAddresses);

         // gender
         if (applicant.gender === 0) {
            applicant.gender = null;
         }

         this.logger.info('getApplicantTransform', JSON.stringify(applicant));

         this.logger.info('moment formatting ' + applicant.dateOfBirth);

         return applicant;
      }
   }

   postApplicantTransform(formData: any) {
      const {
         // probity
         isProbity,
         probityConnectionTypeId,
         probityTitle,
         probityFirstName,
         probityLastName,
         probityRelationship,
         probityEmployeeRole,
         probityPositionOnBoard,
         probityJobTitle,

         // birthDate
         dateOfBirth,

         // currentAddress
         addressId,
         applicantId,
         addressTypeId,
         addressLine1,
         addressLine2,
         addressLine3,
         city,
         county,
         country,
         postCode,
         durationAtCurrentProperty,

         // previousAddresses
         previousAddresses,

         criminalConvictions,

         ...rest
      } = formData;

      let leadApplicant = rest;

      // probity
      leadApplicant.probity = {
         probityConnectionTypeId: probityConnectionTypeId,
         probityPositionOnBoard: probityPositionOnBoard,
         probityJobTitle: probityJobTitle,
         probityTitle: probityTitle,
         probityFirstName: probityFirstName,
         isAssociated: isProbity,
         probityLastName: probityLastName,
         probityEmployeeRole: probityEmployeeRole,
         probityRelationship: probityRelationship,
      };

      // birthDate
      leadApplicant.birthDate = this.processDate(dateOfBirth);

      // currentAddress
      leadApplicant.currentAddress = {
         addressId: addressId,
         applicantId: applicantId,
         addressTypeId: addressTypeId,
         addressLine1: addressLine1,
         addressLine2: addressLine2,
         addressLine3: addressLine3,
         addressCity: city,
         addressStateOrProvince: county,
         addressCountry: country,
         addressPostalCode: postCode,
         durationMonths: durationAtCurrentProperty - Math.floor(durationAtCurrentProperty / 12) * 12,
         durationYears: Math.floor(durationAtCurrentProperty / 12),
      };

      leadApplicant.previousAddresses = this.mapPostPreviousAddresses(previousAddresses);

      leadApplicant.criminalConvictions =
         !criminalConvictions || (criminalConvictions.length === 1 && criminalConvictions[0] === null) ? [] : criminalConvictions;

      this.logger.info('leadApplicant transform', JSON.stringify(leadApplicant));

      leadApplicant = JSON.stringify(leadApplicant);

      return leadApplicant;
   }

   getJointApplicantTransform(response: HttpResponse<any>): any {
      this.logger.info('getJointApplicantTransform start');

      if (response.status === 200) {
         const applicantResponse = response.body;
         console.log('joint applicant apiModel');
         console.log(applicantResponse);

         // use rest to pull out all properties already mapped to controls, re-assign the exclusions
         const { probity, birthDate, currentAddress, previousAddresses, ...rest } = applicantResponse;

         const jointApplicant = rest;

         // probity
         if (probity) {
            jointApplicant.isAssociated = probity.isAssociated;
            jointApplicant.probityConnectionTypeId = probity.probityConnectionTypeId;
            jointApplicant.probityTitle = probity.probityTitle;
            jointApplicant.probityFirstName = probity.probityFirstName;
            jointApplicant.probityLastName = probity.probityLastName;
            jointApplicant.probityRelationship = probity.probityRelationship;
            jointApplicant.probityEmployeeRole = probity.probityEmployeeRole;
            jointApplicant.probityPositionOnBoard = probity.probityPositionOnBoard;
            jointApplicant.probityJobTitle = probity.probityJobTitle;
         }

         // dateOfBirth
         jointApplicant.dateOfBirth = moment.utc(birthDate);

         if (jointApplicant.dateOfBirth == '0001-01-01T06:00:00Z') {
            jointApplicant.dateOfBirth = null;
         }

         jointApplicant.dateOfBirth = jointApplicant.dateOfBirth.add(6, 'h').format();

         // currentAddress set as the default and also assign to be used onChange
         if (currentAddress != null) {
            jointApplicant.addressTypeId = currentAddress.addressTypeId;
            jointApplicant.applicationContactAddressId = currentAddress.addressId;
            jointApplicant.applicationContactAddress = currentAddress.applicationContactAddress;
            jointApplicant.addressLine1 = currentAddress.addressLine1;
            jointApplicant.addressLine2 = currentAddress.addressLine2;
            jointApplicant.addressLine3 = currentAddress.addressLine3;
            jointApplicant.city = currentAddress.addressCity;
            jointApplicant.county = currentAddress.addressStateOrProvince;
            jointApplicant.country = currentAddress.addressCountry;
            jointApplicant.postCode = currentAddress.addressPostalCode;
            jointApplicant.durationAtCurrentProperty = this.currentPropertyDuration(currentAddress.durationYears, currentAddress.durationMonths);

            //stored
            jointApplicant.jointApplicantAddressTypeId = currentAddress.addressTypeId;
            jointApplicant.jointApplicantApplicationContactAddressId = currentAddress.addressId;
            jointApplicant.jointApplicantApplicationContactAddress = currentAddress.applicationContactAddress;
            jointApplicant.jointApplicantAddressLine1 = currentAddress.addressLine1;
            jointApplicant.jointApplicantAddressLine2 = currentAddress.addressLine2;
            jointApplicant.jointApplicantAddressLine3 = currentAddress.addressLine3;
            jointApplicant.jointApplicantCity = currentAddress.addressCity;
            jointApplicant.jointApplicantCounty = currentAddress.addressStateOrProvince;
            jointApplicant.jointApplicantCountry = currentAddress.addressCountry;
            jointApplicant.jointApplicantPostCode = currentAddress.addressPostalCode;
            jointApplicant.jointApplicantDurationAtCurrentProperty = this.currentPropertyDuration(
               currentAddress.durationYears,
               currentAddress.durationMonths
            );
         }

         // previousAddresses
         jointApplicant.previousAddresses = this.mapGetPreviousAddresses(previousAddresses);

         // gender
         if (jointApplicant.gender === 0) {
            jointApplicant.gender = null;
         }

         // criminalConvictions
         if (!jointApplicant.criminalConvictions || jointApplicant.criminalConvictions.length === 0) {
            jointApplicant.criminalConvictions = [null]; //init for formly
         }

         this.logger.info('getJointApplicantTransform', JSON.stringify(jointApplicant));

         return jointApplicant;
      } else if (response.status === 404) {
         // not found
         const jointApplicant = {
            typeId: '505c085b-17a1-e611-80e3-c4346bacde44', // joint jointApplicant type
         };

         return jointApplicant;
      } else {
         return null;
      }
   }

   postJointApplicantTransform(formData: any, leadApplicantCurrentAddress: any) {
      const {
         //remove joint applicant keys

         // probity
         isAssociated,
         probityConnectionTypeId,
         probityTitle,
         probityFirstName,
         probityLastName,
         probityRelationship,
         probityEmployeeRole,
         probityPositionOnBoard,
         probityJobTitle,

         // birthDate
         dateOfBirth,

         // currentAddress
         addressId,
         applicantId,
         addressTypeId,
         addressLine1,
         addressLine2,
         addressLine3,
         city,
         county,
         country,
         postCode,
         durationAtCurrentProperty,

         // previousAddresses
         previousAddresses,

         criminalConvictions,

         //passbacks
         employmentYears,

         ...rest
      } = formData;

      this.logger.info('jointApplicant vm', formData);

      let jointApplicant = rest;

      // probity
      jointApplicant.probity = {
         probityConnectionTypeId: probityConnectionTypeId,
         probityPositionOnBoard: probityPositionOnBoard,
         probityJobTitle: probityJobTitle,
         probityTitle: probityTitle,
         probityFirstName: probityFirstName,
         isAssociated: isAssociated,
         probityLastName: probityLastName,
         probityEmployeeRole: probityEmployeeRole,
         probityRelationship: probityRelationship,
      };

      jointApplicant.monthsAtEmploymentSituation = employmentYears;

      // birthDate
      jointApplicant.birthDate = this.processDate(dateOfBirth);

      // currentAddress
      jointApplicant.currentAddress = {
         addressId: addressId,
         applicantId: applicantId,
         addressTypeId: addressTypeId,
         addressLine1: addressLine1,
         addressLine2: addressLine2,
         addressLine3: addressLine3,
         addressCity: city,
         addressStateOrProvince: county,
         addressCountry: country,
         addressPostalCode: postCode,
         durationMonths: durationAtCurrentProperty - Math.floor(durationAtCurrentProperty / 12) * 12,
         durationYears: Math.floor(durationAtCurrentProperty / 12),
      };

      jointApplicant.previousAddresses = this.mapPostPreviousAddresses(previousAddresses);

      jointApplicant.criminalConvictions =
         !criminalConvictions || (criminalConvictions.length === 1 && criminalConvictions[0] === null) ? [] : criminalConvictions;

      if (jointApplicant.isTheAddressTheSame) {
         jointApplicant.currentAddress = {
            addressTypeId: leadApplicantCurrentAddress.addressTypeId, // pass back value recieved, not set in portal
            addressId: leadApplicantCurrentAddress.applicationContactAddressId, // pass back value recieved, not set in portal
            applicationContactAddress: leadApplicantCurrentAddress.applicationContactAddress, // pass back value recieved, not set in portal
            addressLine1: leadApplicantCurrentAddress.addressLine1,
            addressLine2: leadApplicantCurrentAddress.addressLine2,
            addressLine3: leadApplicantCurrentAddress.addressLine3,
            addressCity: leadApplicantCurrentAddress.city,
            addressStateOrProvince: leadApplicantCurrentAddress.county,
            addressCountry: leadApplicantCurrentAddress.country,
            addressPostalCode: leadApplicantCurrentAddress.postCode,
            durationYears: Math.floor(leadApplicantCurrentAddress.durationAtCurrentProperty / 12),
            durationMonths:
               leadApplicantCurrentAddress.durationAtCurrentProperty - Math.floor(leadApplicantCurrentAddress.durationAtCurrentProperty / 12) * 12,
         };
      }

      this.logger.info('jointApplicant transform', JSON.stringify(jointApplicant));

      jointApplicant = JSON.stringify(jointApplicant);
      return jointApplicant;
   }

   getAdditionalResidentsTransform(response: HttpResponse<any>, applicationId: string) {
      this.logger.info('getAdditionalResidentsTransform start 123', response.body);

      let residents: any;

      if (response.status === 200) {
         // success
         residents = response.body;

         this.logger.info('getAdditionalResidentsTransform after Json parsing', residents);

         residents.additionalResidents = this.mapResidents(residents.additionalResidents);

         this.logger.info('getAdditionalResidentsTransform after', residents);

         // setting applicationId on the response here
         residents.applicationId = applicationId;

         return residents;
      } else if (response.status === 404) {
         // not found
         residents = {};

         return residents;
      } else {
         return null;
      }
   }

   postAdditionalResidentsTransform(formData: any) {
      this.logger.info('postAdditionalResidentsTransform formData', formData);

      if (formData.additionalResidents && formData.additionalResidents[0] === null) {
         formData.additionalResidents = [];
      } else if (formData.additionalResidents) {
         formData.additionalResidents = this.mapResidents(formData.additionalResidents);
      } else {
         formData.additionalResidents = [];
      }

      this.logger.info('postAdditionalResidentsTransform after ' + JSON.stringify(formData));

      formData = JSON.stringify(formData);

      return formData;
   }

   getSafeguardingTransform(response: HttpResponse<any>) {
      this.logger.info('raw safeguarding data: ', response.body);

      let safeguarding: any;

      if (response.status === 200) {
         // success
         safeguarding = response.body;

         this.logger.info('transformed safeguarding data', safeguarding);

         return safeguarding;
      } else if (response.status === 404) {
         // not found
         return {};
      } else {
         return null;
      }
   }

   getReferencesTransform(response: HttpResponse<any>) {
      let references: any;

      if (response.status === 200) {
         references = response.body;

         this.logger.info('References apiModel', references);

         const returnValue: any = {
            referenceOneApplicationRefereeId: null,
            referenceOneApplicantAddressId: null,
            referenceOneApplicantId: null,
            referenceOneType: null,
            referenceOneSubType: null,
            referenceOneTitle: null,
            referenceOneFirstName: null,
            referenceOneLastName: null,
            referenceOneOrganisationName: null,
            referenceOneEmail: null,
            referenceOnePhone: null,
            referenceTwoApplicationRefereeId: null,
            referenceTwoApplicantAddressId: null,
            referenceTwoApplicantId: null,
            referenceTwoType: null,
            referenceTwoSubType: null,
            referenceTwoTitle: null,
            referenceTwoFirstName: null,
            referenceTwoLastName: null,
            referenceTwoOrganisationName: null,
            referenceTwoEmail: null,
            referenceTwoPhone: null,
            addresses: [],
         };

         if (references.referees && references.referees.length > 0) {
            const referee = references.referees[0];
            returnValue.referenceOneApplicationRefereeId = referee.applicationRefereeId;
            returnValue.referenceOneApplicantAddressId = referee.applicantAddressId;
            returnValue.referenceOneApplicantId = referee.applicantId;
            returnValue.referenceOneType = referee.refereeTypeId;
            returnValue.referenceOneSubType = referee.contactType ? referee.contactType : null;
            returnValue.referenceOneTitle = referee.title ? referee.title : null;
            returnValue.referenceOneFirstName = referee.firstName;
            returnValue.referenceOneLastName = referee.lastName;
            returnValue.referenceOneOrganisationName = referee.organisationName;
            returnValue.referenceOneEmail = referee.emailAddress;
            returnValue.referenceOnePhone = referee.phoneNumber;
         }

         if (references.referees && references.referees.length > 1) {
            const referee = references.referees[1];
            returnValue.referenceTwoApplicationRefereeId = referee.applicationRefereeId;
            returnValue.referenceTwoApplicantAddressId = referee.applicantAddressId;
            returnValue.referenceTwoApplicantId = referee.applicantId;
            returnValue.referenceTwoType = referee.refereeTypeId;
            returnValue.referenceTwoSubType = referee.contactType ? referee.contactType : null;
            returnValue.referenceTwoTitle = referee.title ? referee.title : null;
            returnValue.referenceTwoFirstName = referee.firstName;
            returnValue.referenceTwoLastName = referee.lastName;
            returnValue.referenceTwoOrganisationName = referee.organisationName;
            returnValue.referenceTwoEmail = referee.emailAddress;
            returnValue.referenceTwoPhone = referee.phoneNumber;
         }

         if (references.addresses && references.addresses.length > 0) {
            references.addresses.forEach((a: any) => {
               returnValue.addresses.push({
                  value: a.addressId,
                  name: this.stringifyAddress(a),
               });
            });
         }

         return returnValue;
      }
   }

   postReferencesTransform(formData: any) {
      let references: any = [
         {
            applicationRefereeId: formData.referenceOneApplicationRefereeId,
            refereeTypeId: formData.referenceOneType,
            contactType: formData.referenceOneSubType,
            emailAddress: formData.referenceOneEmail,
            organisationName: formData.referenceOneOrganisationName,
            title: formData.referenceOneTitle,
            firstName: formData.referenceOneFirstName,
            lastName: formData.referenceOneLastName,
            phoneNumber: formData.referenceOnePhone,
            applicantAddressId: formData.referenceOneApplicantAddressId,
            applicantId: formData.referenceOneApplicantId,
         },
         {
            applicationRefereeId: formData.referenceTwoApplicationRefereeId,
            refereeTypeId: formData.referenceTwoType,
            contactType: formData.referenceTwoSubType,
            emailAddress: formData.referenceTwoEmail,
            organisationName: formData.referenceTwoOrganisationName,
            title: formData.referenceTwoTitle,
            firstName: formData.referenceTwoFirstName,
            lastName: formData.referenceTwoLastName,
            phoneNumber: formData.referenceTwoPhone,
            applicantAddressId: formData.referenceTwoApplicantAddressId,
            applicantId: formData.referenceTwoApplicantId,
         },
      ];

      this.logger.info('references transform', JSON.stringify(references));

      references = JSON.stringify(references);

      return references;
   }

   getJointReferencesTransform(response: HttpResponse<any>) {
      let jointReferences: any;

      if (response.status === 200) {
         jointReferences = response.body;

         this.logger.info('Joint References apiModel', jointReferences);

         const returnValue: any = {
            referenceOneApplicationRefereeId: null,
            referenceOneApplicantAddressId: null,
            referenceOneApplicantId: null,
            referenceOneType: null,
            referenceOneSubType: null,
            referenceOneTitle: null,
            referenceOneFirstName: null,
            referenceOneLastName: null,
            referenceOneOrganisationName: null,
            referenceOneEmail: null,
            referenceOnePhone: null,
            referenceTwoApplicationRefereeId: null,
            referenceTwoApplicantAddressId: null,
            referenceTwoApplicantId: null,
            referenceTwoType: null,
            referenceTwoSubType: null,
            referenceTwoTitle: null,
            referenceTwoFirstName: null,
            referenceTwoLastName: null,
            referenceTwoOrganisationName: null,
            referenceTwoEmail: null,
            referenceTwoPhone: null,
            addresses: [],
         };

         if (jointReferences.referees && jointReferences.referees.length > 0) {
            const referee = jointReferences.referees[0];
            returnValue.referenceOneApplicationRefereeId = referee.applicationRefereeId;
            returnValue.referenceOneApplicantAddressId = referee.applicantAddressId;
            returnValue.referenceOneApplicantId = referee.applicantId;
            returnValue.referenceOneType = referee.refereeTypeId;
            returnValue.referenceOneSubType = referee.contactType ? referee.contactType : null;
            returnValue.referenceOneTitle = referee.title ? referee.title : null;
            returnValue.referenceOneFirstName = referee.firstName;
            returnValue.referenceOneLastName = referee.lastName;
            returnValue.referenceOneOrganisationName = referee.organisationName;
            returnValue.referenceOneEmail = referee.emailAddress;
            returnValue.referenceOnePhone = referee.phoneNumber;
         }

         if (jointReferences.referees && jointReferences.referees.length > 1) {
            const referee = jointReferences.referees[1];
            returnValue.referenceTwoApplicationRefereeId = referee.applicationRefereeId;
            returnValue.referenceTwoApplicantAddressId = referee.applicantAddressId;
            returnValue.referenceTwoApplicantId = referee.applicantId;
            returnValue.referenceTwoType = referee.refereeTypeId;
            returnValue.referenceTwoSubType = referee.contactType ? referee.contactType : null;
            returnValue.referenceTwoTitle = referee.title ? referee.title : null;
            returnValue.referenceTwoFirstName = referee.firstName;
            returnValue.referenceTwoLastName = referee.lastName;
            returnValue.referenceTwoOrganisationName = referee.organisationName;
            returnValue.referenceTwoEmail = referee.emailAddress;
            returnValue.referenceTwoPhone = referee.phoneNumber;
         }

         if (jointReferences.addresses && jointReferences.addresses.length > 0) {
            jointReferences.addresses.forEach((a: any) => {
               returnValue.addresses.push({
                  value: a.addressId,
                  name: this.stringifyAddress(a),
               });
            });
         }

         return returnValue;
      }
   }

   postJointReferencesTransform(formData: any, jointModel: any) {
      console.log(formData);
      console.log(jointModel);
      let jointReferences: any = [
         {
            applicationRefereeId: formData.referenceOneApplicationRefereeId,
            refereeTypeId: formData.referenceOneType,
            contactType: formData.referenceOneSubType,
            emailAddress: formData.referenceOneEmail,
            organisationName: formData.referenceOneOrganisationName,
            title: formData.referenceOneTitle,
            firstName: formData.referenceOneFirstName,
            lastName: formData.referenceOneLastName,
            phoneNumber: formData.referenceOnePhone,
            applicantAddressId: formData.referenceOneApplicantAddressId,
            applicantId: jointModel.id,
            // "applicantId": formData.referenceOneApplicantId
         },
         {
            applicationRefereeId: formData.referenceTwoApplicationRefereeId,
            refereeTypeId: formData.referenceTwoType,
            contactType: formData.referenceTwoSubType,
            emailAddress: formData.referenceTwoEmail,
            organisationName: formData.referenceTwoOrganisationName,
            title: formData.referenceTwoTitle,
            firstName: formData.referenceTwoFirstName,
            lastName: formData.referenceTwoLastName,
            phoneNumber: formData.referenceTwoPhone,
            applicantAddressId: formData.referenceTwoApplicantAddressId,
            applicantId: jointModel.id,
            // "applicantId": formData.referenceTwoApplicantId
         },
      ];

      this.logger.info('jointReferences transform', JSON.stringify(jointReferences));

      jointReferences = JSON.stringify(jointReferences);

      return jointReferences;
   }

   mapPostPreviousAddresses(addresses: any) {
      if (!addresses || addresses[0] === null) {
         //this is inferred by an empty prev address array
         return [];
      }
      console.log('unformatted addresses', addresses);
      const formatted: any = [];

      addresses.forEach((address: any) => {
         if (address.durationAtPreviousProperty != null) {
            // remove empty rows added by the sechema form library.
            const durationYears = Math.floor(address.durationAtPreviousProperty / 12);
            const durationMonths = address.durationAtPreviousProperty - durationYears * 12;

            const formattedAddress = {
               addressTypeId: address.addressTypeId,
               applicationContactAddress: address.applicationContactAddress,
               addressId: address.applicationContactAddressId,
               addressLine1: address.addressLine1,
               addressLine2: address.addressLine2,
               addressLine3: address.addressLine3,
               addressCity: address.city,
               addressStateOrProvince: address.county,
               addressCountry: address.country,
               addressPostalCode: address.postCode,
               durationMonths: durationMonths,
               durationYears: durationYears,
            };

            if (!address.applicationContactAddressId) {
               formattedAddress.addressId = null;
            } else {
               formattedAddress.addressId = address.applicationContactAddressId;
            }

            if (!address.applicationContactAddress) {
               formattedAddress.applicationContactAddress = null;
            } else {
               formattedAddress.applicationContactAddress = address.applicationContactAddress;
            }

            formatted.push(formattedAddress);
         }
      });
      console.log('formatted addresses', formatted);
      return formatted;
   }

   mapGetPreviousAddresses(addresses: any) {
      const formatted: any[] = [];

      if (addresses !== null && addresses.length > 0) {
         addresses.forEach((address: any) => {
            const formattedAddress = {
               addressTypeId: address.addressTypeId,
               applicationContactAddress: address.applicationContactAddress,
               applicationContactAddressId: address.addressId,
               addressLine1: address.addressLine1,
               addressLine2: address.addressLine2,
               addressLine3: address.addressLine3,
               city: address.addressCity,
               county: address.addressStateOrProvince,
               country: address.addressCountry,
               postCode: address.addressPostalCode,
               durationAtPreviousProperty: this.currentPropertyDuration(address.durationYears, address.durationMonths),
            };

            formatted.push(formattedAddress);
         });
      }

      return formatted;
   }

   currentPropertyDuration(years: number, months: number) {
      const totalMonths = years * 12 + months;
      return totalMonths;
   }

   // Prevent date from losing a day when going from GMT to UTC
   processDate(dateToProcess: any) {
      this.logger.info('processDate date in', dateToProcess);

      if (moment(dateToProcess).format('H') === '23' || moment(dateToProcess).format('H') === '0') {
         const utcDate = moment(dateToProcess).add(1, 'h');
         dateToProcess = utcDate.format('YYYY-MM-DDTHH:mm:ss[Z]');
      }

      this.logger.info('processDate date out', dateToProcess);

      return dateToProcess;
   }

   mapResidents(residents: any) {
      this.logger.info('mapResidents before', residents);

      const formatted: any = [];

      residents.forEach((resident: any) => {
         this.logger.info('additional resident', resident);

         // Daylight Savings Time fix
         if (moment(resident.birthDate).format('H') === '23' || moment(resident.birthDate).format('H') === '0') {
            this.logger.info('its the day before');
            resident.birthDate = moment.utc(resident.birthDate);
            resident.birthDate = resident.birthDate.add(1, 'h').format('YYYY-MM-DDTHH:mm:ss[Z]');
         }

         if (resident.middleName === null) {
            resident.middleName = '';
         }

         formatted.push(resident);
      });

      this.logger.info('mapResidents after', formatted);

      return formatted;
   }

   stringifyAddress(address: any) {
      let retval: any;

      if (address.addressLine1) {
         retval = address.addressLine1 + ' ';
      }
      if (address.addressLine2) {
         retval += address.addressLine2 + ', ';
      }
      if (address.addressLine3) {
         retval += address.addressLine3 + ', ';
      }
      if (address.addressCity) {
         retval += address.addressCity + ', ';
      }
      if (address.addressStateOrProvince) {
         retval += address.addressStateOrProvince + ', ';
      }
      if (address.addressCountry) {
         retval += address.addressCountry + ', ';
      }
      if (address.addressPostalCode) {
         retval += address.addressPostalCode + ', ';
      }

      return retval.replace(/,\s*$/, '');
   }
}
